/**
 * Field schema for the CD additional-information capture form (scenarios 5 & 7).
 *
 * Keyed by the Webflow `data-name` attribute the designer placed on each input.
 * The lead-tracker discovers fields inside `[data-component="cd-outcome-additional-information"]`
 * using both `data-name=` and `name=` selectors.
 *
 * `rules` is the array passed to FormatValidators.validateWithRules() — reuses the same
 * registry that drives main-form validation.
 *
 * `payloadKey` is the snake_case key used in the JSONB payload sent to the edge function
 * and the new dcapi endpoint. Generic naming — no "stop-and-go" anywhere.
 */

import type { ValidationRule } from '../formatting/types/formatting-types';

export interface AdditionalInfoFieldRule {
  /** Webflow data-name attribute on the input element */
  webflowName: string;
  /** Generic snake_case key written into the payload JSON */
  payloadKey: string;
  /** Validation rules to run against this field's value */
  rules: ValidationRule[];
}

export const ADDITIONAL_INFO_FIELDS: AdditionalInfoFieldRule[] = [
  {
    webflowName: 'Drivers License Number',
    payloadKey: 'drivers_license_number',
    rules: [
      { type: 'required' },
      { type: 'length', params: { min: 4, max: 25 } },
    ],
  },
  {
    webflowName: 'State',
    payloadKey: 'drivers_license_state',
    rules: [{ type: 'required' }],
  },
  {
    webflowName: 'time-at-address',
    payloadKey: 'time_at_address',
    rules: [{ type: 'required' }],
  },
  {
    webflowName: 'Job Title',
    payloadKey: 'job_title',
    rules: [
      { type: 'required' },
      { type: 'length', params: { min: 2, max: 100 } },
    ],
  },
  {
    webflowName: 'Company Name',
    payloadKey: 'company_name',
    rules: [
      { type: 'required' },
      { type: 'length', params: { min: 2, max: 100 } },
    ],
  },
  {
    webflowName: 'Work Phone Number',
    payloadKey: 'work_phone_number',
    rules: [{ type: 'required' }, { type: 'phone' }],
  },
  {
    webflowName: 'Time at Job',
    payloadKey: 'time_at_job',
    rules: [{ type: 'required' }],
  },
  {
    webflowName: 'Pay Frequency',
    payloadKey: 'pay_frequency',
    rules: [{ type: 'required' }],
  },
  {
    webflowName: 'Next Pay Date',
    payloadKey: 'next_pay_date',
    rules: [
      { type: 'required' },
      { type: 'future_date', params: { maxDaysAhead: 60 } },
    ],
  },
  {
    webflowName: 'Direct Deposit',
    payloadKey: 'direct_deposit',
    rules: [{ type: 'required' }],
  },
  {
    webflowName: 'Military',
    payloadKey: 'military',
    rules: [{ type: 'required' }],
  },
  {
    webflowName: 'Bank Name',
    payloadKey: 'bank_name',
    rules: [
      { type: 'required' },
      { type: 'length', params: { min: 2, max: 100 } },
    ],
  },
  {
    webflowName: 'Bank account number',
    payloadKey: 'bank_account_number',
    rules: [
      { type: 'required' },
      { type: 'pattern', params: { pattern: '^\\d{4,17}$', message: '*Account number must be 4–17 digits' } },
    ],
  },
  {
    webflowName: 'routing number',
    payloadKey: 'routing_number',
    rules: [
      { type: 'required' },
      { type: 'pattern', params: { pattern: '^\\d{9}$', message: '*Routing number must be 9 digits' } },
    ],
  },
  {
    webflowName: 'Banking account type',
    payloadKey: 'bank_account_type',
    rules: [{ type: 'required' }],
  },
  {
    webflowName: 'Estimated Credit Score',
    payloadKey: 'estimated_credit_score',
    rules: [{ type: 'required' }],
  },
];

export function findFieldRuleByWebflowName(name: string): AdditionalInfoFieldRule | undefined {
  return ADDITIONAL_INFO_FIELDS.find(
    (f) => f.webflowName.toLowerCase() === name.toLowerCase()
  );
}
